import { Box, ButtonProps, Flex, HStack, Input, Text, Tooltip, VStack } from '@chakra-ui/react'
import {
  capitalizeFirstLetterOfEachWord,
  colors,
  CustomElement,
  EditorVersion,
  getFullName,
  PopulatedNode,
  populateTemplate,
  Template,
  TemplateKey,
  templateTypeNames,
  UpdateCallback,
  UserFieldItem,
  WithId,
} from '@hb/shared'

import { useCallback, useContext, useMemo, useState } from 'react'
import { useSlate } from 'slate-react'
import { ActionButton, BlueButton, SolidActionButton } from '../Buttons'
import { DefaultModal } from '../Modals/DefaultModal'
import { TextEditorContext } from '../RichText/context'
import { SubmitText } from '../RichText/hooks'
import { useTemplateData, useTemplateView } from './contexts'

import { PopUpMessageContext } from '@hb/react'
import { useProfile } from '../../contexts/user/ProfileContext'
import { ConfirmResendConsentForm } from './ConfirmResendConsentForm'
import { TemplateErrorsAlert } from './TemplateErrorsAlert'
import { TemplateViewBodyProps } from './types'

export const useOnSubmit = (
  templateType: TemplateKey,
  template: WithId<Template> | null,
  editorVersion: EditorVersion,
  submitText?: TemplateViewBodyProps['submitText'],
) => {
  const { onSubmit, templateId } = useTemplateView()
  const { shortcutArgs } = useTemplateData()
  const [submitConfirm, setSubmitConfirm] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [displayedErrors, setDisplayedErrors] = useState<Array<PopulatedNode> | null>(null)
  const [submittedText, setSubmittedText] = useState<Array<CustomElement & UserFieldItem> | null>(
    null,
  )
  const { processResponse } = useContext(PopUpMessageContext)
  const handleSubmit = useCallback(
    async (name: string): Promise<UpdateCallback> => {
      if (submitConfirm) {
        if (!submittedText) {
          return processResponse({
            error: 'Internal error - missing submitted text',
          })
        }
        if (!name) {
          return processResponse({
            error: 'Document name cannot be empty',
          })
        }
        const { populated, errors } = populateTemplate(
          { templateText: submittedText },
          shortcutArgs,
        )
        if (errors.length) {
          setDisplayedErrors(errors)
          return processResponse({
            error: 'Errors in template when submitting',
          })
        }
        if (!onSubmit) {
          return processResponse({
            error: 'Internal error - missing submit function',
          })
        }
        setSubmitLoading(true)
        try {
          const res = await onSubmit(
            populated,
            editorVersion,
            templateId,
            name,
            shortcutArgs.plan?.id ?? null,
          )
          setSubmitLoading(false)
          setSubmitConfirm(false)
          return processResponse(res)
        } catch (err: any) {
          console.log('logging error', err)
          console.error(err)
          setSubmitLoading(false)
          // showError('Failed to submit document', err?.message)
          return {
            error: err?.message || 'Failed to submit document',
          }
        }
      }
      setSubmitConfirm(true)
      return { success: 'confirm?' }
    },
    [
      onSubmit,
      submitConfirm,
      shortcutArgs,
      processResponse,
      editorVersion,
      submittedText,
      templateId,
    ],
  )

  const handleSubmitClick = useCallback((text: (CustomElement & UserFieldItem)[]) => {
    setSubmittedText(text)
    setSubmitConfirm(true)
  }, [])

  const submitButtonText = useMemo(() => submitText?.submit || 'Submit', [submitText])

  return useMemo(
    () => ({
      submitLoading,
      displayedErrors,
      handleSubmit,
      templateType,
      submitButtonText,
      submitConfirm,
      handleSubmitClick,
      onCancel: () => {
        setSubmitConfirm(false)
        setDisplayedErrors(null)
      },
    }),
    [
      submitLoading,
      displayedErrors,
      handleSubmit,
      templateType,
      submitButtonText,
      submitConfirm,
      handleSubmitClick,
    ],
  )
}

const DocumentSubmitModal = ({
  onCancel,
  templateType,
  template,
  handleSubmit,
  isSubmitting,
}: {
  onCancel: () => void
  templateType: TemplateKey
  template: WithId<Template> | null
  handleSubmit: (name: string) => Promise<UpdateCallback>
  isSubmitting: boolean
}) => {
  // const {} = useContext(TextEditorContext)
  const { existingTemplateConsentFormStatus } = useTemplateView()
  const { user, userId } = useProfile()
  const { processResponse } = useContext(PopUpMessageContext)
  const fullName = useMemo(() => getFullName(user), [user])
  const documentTypeName = useMemo(() => templateTypeNames[templateType], [templateType])
  const [documentName, setDocumentName] = useState(
    template?.name ?? capitalizeFirstLetterOfEachWord(documentTypeName),
  )
  return (
    <DefaultModal
      onClose={onCancel}
      overlayHeader
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isOpen
      render={({ onClose }) => (
        <VStack bg="gray.50" w="100%" align="flex-start" spacing={2}>
          {templateType === 'assessments' ? (
            <Flex flexFlow="column" py={2} px={4}>
              <Text fontFamily="Comfortaa" fontSize="lg" fontWeight={600} color="green.500">
                Confirm Send
              </Text>
              <Text color="gray.600">Send this assessment to {fullName}?</Text>
            </Flex>
          ) : (
            <>
              <Flex flexFlow="column" w="100%" py={2} px={4}>
                <Text color="gray.600" fontSize="sm">
                  Document name (visible to{' '}
                  {templateType === 'practiceContract' ? 'provider' : 'patient'}):
                </Text>
                <Input
                  value={documentName}
                  onChange={e => setDocumentName(e.target.value)}
                  background="white"
                  _focus={{
                    boxShadow: 'none',
                    border: `1px solid ${colors.green.hex}`,
                  }}
                  placeholder="Document Name"
                />
              </Flex>
              {existingTemplateConsentFormStatus && templateType === 'consentForm' && userId ? (
                <Box
                  bg="white"
                  pt={2}
                  borderBottom="1px solid"
                  borderTop="1px solid"
                  borderColor="gray.200">
                  <ConfirmResendConsentForm
                    patientId={userId}
                    existingTemplateConsentFormStatus={existingTemplateConsentFormStatus}
                  />
                </Box>
              ) : null}
            </>
          )}
          <HStack pb={2} px={3} w="100%" justify="flex-end">
            <ActionButton onClick={onCancel}>Cancel</ActionButton>
            <SolidActionButton
              isLoading={isSubmitting}
              onClick={() => {
                handleSubmit(documentName).then(res => {
                  if (res.error) processResponse(res)
                  else onClose()
                })
              }}>
              {existingTemplateConsentFormStatus ? 'Re-send' : 'Send'} {documentTypeName}
            </SolidActionButton>
          </HStack>
        </VStack>
      )}
    />
  )
}

export const TextEditorSubmitButton = ({
  submitButtonText,
  onCancel,
  submitConfirm,
  handleSubmit,
  handleSubmitClick,
  templateType,
  submitLoading,
  displayedErrors,
  template,
  ...props
}: ButtonProps & ReturnType<typeof useOnSubmit> & { template: WithId<Template> | null }) => {
  const editor = useSlate()
  const { mode } = useContext(TextEditorContext)
  const isEnabled = mode === 'View' && !submitLoading
  return (
    <>
      {/* {submitConfirm ? (
        <ActionButton
          colorScheme="gray"
          size="xs"
          isDisabled={submitLoading}
          mt={0}
          mr={2}
          opacity={0.9}
          pointerEvents={'auto'}
          onClick={onCancel}
        >
          Cancel
        </ActionButton>
      ) : null} */}
      <Tooltip
        placement="top"
        hasArrow
        label={mode === 'Edit' ? 'Must be in View mode to submit' : null}>
        <BlueButton
          mt={0}
          size="xs"
          style={{ fontSize: '13px' }}
          onClick={isEnabled ? () => handleSubmitClick(editor.children) : undefined}
          isLoading={submitLoading}
          opacity={isEnabled ? '1' : '0.5'}
          {...props}>
          {submitButtonText}
        </BlueButton>
      </Tooltip>
      {submitConfirm ? (
        <DocumentSubmitModal
          templateType={templateType}
          template={template}
          isSubmitting={submitLoading}
          onCancel={onCancel}
          handleSubmit={handleSubmit}
        />
      ) : null}
      <TemplateErrorsAlert errors={displayedErrors} onCancel={onCancel} />
    </>
  )
}

export const PdfViewSubmitButton = ({
  submitText,
  submitLoading,
  template,
  templateType,
  handleSubmit,
}: {
  submitText: SubmitText | undefined
  template: WithId<Template> | null
  submitLoading: boolean
  templateType: TemplateKey
  handleSubmit: (name: string) => Promise<UpdateCallback>
}) => {
  const [submitConfirm, setSubmitConfirm] = useState(false)
  return (
    <Flex align="center">
      <BlueButton
        size="xs"
        style={{ fontSize: '13px' }}
        onClick={() => setSubmitConfirm(true)}
        isLoading={submitLoading}>
        {submitConfirm ? (submitText?.confirm ?? 'Confirm') : (submitText?.submit ?? 'Submit')}
      </BlueButton>
      {submitConfirm ? (
        <DocumentSubmitModal
          template={template}
          templateType={templateType}
          isSubmitting={submitLoading}
          onCancel={() => setSubmitConfirm(false)}
          handleSubmit={handleSubmit}
        />
      ) : null}
    </Flex>
  )
}
